import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Truck, MapPin, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { fmtMoney, HAUL_STATUSES, rpmColor } from "@/lib/theme";
import { StatCard } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const statusMeta = (key) => HAUL_STATUSES.find((s) => s.key === key) || HAUL_STATUSES[0];

function locLabel(loc) {
  if (!loc) return "—";
  if (loc.city || loc.state) return [loc.city, loc.state].filter(Boolean).join(", ");
  return loc.label || "—";
}

export default function Hauls() {
  const navigate = useNavigate();
  const [hauls, setHauls] = useState(null);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const [list, s] = await Promise.all([api("/hauls?limit=500"), api("/hauls/summary/stats")]);
      setHauls(list);
      setStats(s);
    } catch {
      setHauls([]);
    }
  }, []);

  useEffect(() => {
    document.title = "Hauls · RigBooks";
    load();
  }, [load]);

  const shown = !hauls ? [] : filter === "all" ? hauls : hauls.filter((h) => h.status === filter);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="YTD Gross" value={fmtMoney(stats?.total_revenue || 0)} accent="#00E676" testID="haul-stat-gross" />
        <StatCard label="Total Miles" value={(stats?.total_miles || 0).toLocaleString()} testID="haul-stat-miles" />
        <StatCard
          label="Avg RPM"
          value={`$${(stats?.avg_rpm || 0).toFixed(2)}`}
          accent={rpmColor(stats?.avg_rpm || 0)}
          testID="haul-stat-rpm"
        />
        <StatCard label="Active" value={stats?.active_hauls ?? 0} sub="booked + in-transit" testID="haul-stat-active" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilter("all")}
            className={`rounded-full border px-4 py-1.5 text-sm font-semibold ${
              filter === "all"
                ? "border-rig-accent bg-rig-accent text-rig-accentText"
                : "border-rig-border bg-rig-surface text-rig-dim hover:text-white"
            }`}
          >
            All
          </button>
          {HAUL_STATUSES.map((s) => (
            <button
              key={s.key}
              onClick={() => setFilter(s.key)}
              data-testid={`haul-filter-${s.key}`}
              className={`rounded-full border px-4 py-1.5 text-sm font-semibold ${
                filter === s.key
                  ? "border-rig-accent bg-rig-accent text-rig-accentText"
                  : "border-rig-border bg-rig-surface text-rig-dim hover:text-white"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <Button
          data-testid="new-haul-btn"
          onClick={() => navigate("/hauls/new")}
          className="bg-rig-accent font-display text-xs font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark"
        >
          <Plus className="mr-1 h-4 w-4" strokeWidth={3} /> New Haul
        </Button>
      </div>

      {!hauls ? (
        <div className="grid gap-4 md:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-40 bg-rig-surface" />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <div className="flex flex-col items-center rounded-2xl border border-dashed border-rig-borderStrong bg-rig-surface p-14 text-center">
          <Truck className="h-12 w-12 text-rig-mute" />
          <p className="mt-4 font-display text-xl uppercase tracking-wide text-white">No hauls yet</p>
          <p className="mt-1 text-sm text-rig-dim">Book your first load to start tracking revenue per mile.</p>
          <Button
            onClick={() => navigate("/hauls/new")}
            className="mt-5 bg-rig-accent font-display text-xs font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark"
          >
            <Plus className="mr-1 h-4 w-4" strokeWidth={3} /> New Haul
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {shown.map((h) => {
            const meta = statusMeta(h.status);
            return (
              <button
                key={h.id}
                onClick={() => navigate(`/hauls/${h.id}`)}
                data-testid={`haul-card-${h.id}`}
                className="rounded-2xl border border-rig-border bg-rig-surface p-5 text-left transition-all hover:border-rig-borderStrong hover:bg-rig-surface2"
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="truncate font-display text-lg font-bold uppercase tracking-wide text-white">
                      {h.broker || "Unassigned broker"}
                    </div>
                    <div className="text-xs text-rig-dim">Load #{h.load_number || "—"}</div>
                  </div>
                  <span
                    className="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider"
                    style={{ background: `${meta.color}22`, color: meta.color }}
                  >
                    {meta.label}
                  </span>
                </div>

                <div className="mt-4 flex items-center gap-2 text-sm text-white">
                  <MapPin className="h-4 w-4 shrink-0 text-rig-accent" />
                  <span className="truncate">{locLabel(h.pickup)}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-rig-mute" />
                  <span className="truncate">{locLabel(h.delivery)}</span>
                </div>

                <div className="mt-4 flex items-end justify-between border-t border-rig-border pt-4">
                  <div>
                    <div className="font-display text-2xl font-bold text-white">{fmtMoney(h.rate_usd)}</div>
                    <div className="text-xs text-rig-dim">{h.miles?.toLocaleString() || 0} mi</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl font-bold" style={{ color: rpmColor(h.rate_per_mile) }}>
                      ${h.rate_per_mile?.toFixed(2)}
                    </div>
                    <div className="text-xs text-rig-dim">per mile</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
